import { Routes } from '@angular/router';
import { userGuard } from '../../common/guards/user.guard';
import { privilegeGuard } from '../../common/guards/privilege-guard';
import { restaurantRoutes } from './restaurant/restaurant.routes';
import { deliveryRoutes } from './delivery/delivery.routes';
import { didoxGuard } from '../../common/guards/didox.guard';
import { moduleBlockedGuard } from '../../common/guards/module-blocked.guard';
import { noPartnerGuard } from '../../common/guards/partner.guard';
import { financeRoutes } from './finance/finance.router';
import {
  EDI_INTEGRATION_MODULE,
  MULTIPRICING_MODULE,
} from '../../common/constants/modules';

export const userMainRoutes: Routes = [
  {
    path: 'user-main',
    loadComponent: () => import('./user-main').then((x) => x.UserMain),
    canActivate: [userGuard],
    canActivateChild: [moduleBlockedGuard, privilegeGuard],
    children: [
      { path: '', redirectTo: '401', pathMatch: 'full' },
      {
        path: '401',
        loadComponent: () =>
          import('../../features/unauth/unauth').then((x) => x.Unauth),
      },
      {
        path: 'blocked',
        loadComponent: () =>
          import('../../features/blocked/blocked.main').then(
            (x) => x.BlockedMain,
          ),
      },
      {
        path: 'cashboxes',
        loadComponent: () =>
          import('./cashboxes/cashboxes').then((x) => x.Cashboxes),
        data: { privileges: ['p44'], moduleName: 'cashboxes' },
      },
      {
        path: 'remains',
        loadComponent: () =>
          import('./warehouse/remains/remains').then((x) => x.Remains),
        data: { moduleName: 'remains' },
      },
      {
        path: 'purchase',

        loadComponent: () =>
          import('./warehouse/operations/purchase/purchase').then(
            (x) => x.Purchase,
          ),
        data: { privileges: ['p15'], moduleName: 'purchase' },
      },
      {
        path: 'new-purchase',
        loadComponent: () =>
          import('./warehouse/operations/purchase/add-purchase/add-purchase').then(
            (x) => x.AddPurchase,
          ),
        data: { privileges: ['p15'], moduleName: 'purchase' },
      },
      {
        path: 'new-purchase/ai',
        loadComponent: () =>
          import('./warehouse/operations/purchase/ai-purchase/ai-purchase').then(
            (x) => x.AiPurchase,
          ),
        data: { privileges: ['p15'], moduleName: 'purchase' },
        // уход во время проводки оборвал бы POST/DELETE (rxMethod отписывается с уничтожением стора).
        // Тип структурный — импорт класса страницы утянул бы её в чанк маршрутов;
        // c = null, если outlet не активирован (упал конструктор страницы) — иначе гард запер бы на ней
        canDeactivate: [
          (c: { store: { accepting(): boolean } } | null) =>
            !c?.store.accepting(),
        ],
      },
      {
        path: 'reduction',
        loadComponent: () =>
          import('./warehouse/operations/reduction/reduction').then(
            (x) => x.Reduction,
          ),
        data: { privileges: ['reduction'], moduleName: 'reduction' },
      },
      {
        path: 'new-reduction',
        loadComponent: () =>
          import('./warehouse/operations/reduction/add-reduction/add-reduction').then(
            (x) => x.AddReduction,
          ),
        data: { privileges: ['reduction'], moduleName: 'reduction' },
      },
      {
        path: 'nocashboxsale',
        loadComponent: () =>
          import('./warehouse/realisation/realisation').then(
            (x) => x.Realisation,
          ),
        data: { privileges: ['p17'], moduleName: 'nocashboxsale' },
      },
      {
        path: 'new-realisation',
        loadComponent: () =>
          import('./warehouse/realisation/add-realisation/add-realisation').then(
            (x) => x.AddRealisation,
          ),
        data: { privileges: ['p17'], moduleName: 'nocashboxsale' },
      },
      {
        path: 'provider',

        loadComponent: () =>
          import('./warehouse/providers/providers').then(
            (x) => x.ProvidersComponent,
          ),
        data: { privileges: ['p23'], moduleName: 'provider' },
      },
      {
        path: 'provider/:id',

        loadComponent: () =>
          import('./warehouse/providers/provider-history/provider-history').then(
            (x) => x.ProviderHistory,
          ),
        data: { privileges: ['p24'], moduleName: 'provider' },
      },
      {
        path: 'inventarization',
        loadComponent: () =>
          import('./warehouse/operations/inventorization/inventorization').then(
            (x) => x.Inventorization,
          ),
        data: { privileges: ['p18'], moduleName: 'inventarization' },
      },
      {
        path: 'inventarization/edit/:id',
        loadComponent: () =>
          import('./warehouse/operations/inventorization/add-inventorization/add-inventorization').then(
            (x) => x.AddInventorization,
          ),
        data: { privileges: ['p18'], moduleName: 'inventarization' },
      },
      {
        path: 'add-inventorization',
        loadComponent: () =>
          import('./warehouse/operations/inventorization/add-inventorization/add-inventorization').then(
            (x) => x.AddInventorization,
          ),
        data: { privileges: ['p18'], moduleName: 'inventarization' },
      },
      {
        path: 'replacement',
        loadComponent: () =>
          import('./warehouse/transfer/transfer').then((x) => x.Transfer),
        data: { privileges: ['p16'], moduleName: 'replacement' },
      },
      {
        path: 'new-replacement',
        loadComponent: () =>
          import('./warehouse/transfer/new-transfer/new-transfer').then(
            (x) => x.NewTransfer,
          ),
        data: { privileges: ['p16'], moduleName: 'replacement' },
      },
      {
        // документ перемещения: просмотр + режим приёмки (?accept=1)
        path: 'replacement/:id',
        loadComponent: () =>
          import('./warehouse/transfer/transfer-detail/transfer-detail').then(
            (x) => x.TransferDetail,
          ),
        data: { privileges: ['p16'], moduleName: 'replacement' },
      },
      {
        path: 'cashiers',
        loadComponent: () =>
          import('./cashiers/cashiers').then((x) => x.Cashiers),
        data: { privileges: ['cashier_add_edit'], moduleName: 'cashiers' },
      },
      {
        path: 'client',
        loadComponent: () => import('./clients/clients').then((x) => x.Clients),
        data: { privileges: ['p21'], moduleName: 'client' },
      },
      {
        path: 'price-management',
        loadComponent: () =>
          import('./warehouse/price-management/price-management').then(
            (x) => x.PriceManagement,
          ),
        data: {
          privileges: ['price_management'],
          moduleName: 'price-management',
        },
      },
      {
        // Viewing price lists is open to every org user; each mutation control
        // is gated on the `price_list_manage` privilege inside the pages.
        path: 'multipricing',
        loadComponent: () =>
          import('./multipricing/multipricing').then((x) => x.Multipricing),
        data: {
          moduleName: MULTIPRICING_MODULE,
        },
      },
      {
        path: 'multipricing/:id',
        loadComponent: () =>
          import('./multipricing/price-list-detail/price-list-detail').then(
            (x) => x.PriceListDetail,
          ),
        data: {
          moduleName: MULTIPRICING_MODULE,
        },
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./settings/settings').then((x) => x.Settings),
        data: { privileges: ['p32'], moduleName: 'settings' },
        children: [
          {
            path: '',
            redirectTo: 'organization',
            pathMatch: 'full',
          },
          {
            path: 'organization',
            loadComponent: () =>
              import('./settings/settings-organization/settings-organization').then(
                (x) => x.SettingsOrganization,
              ),
          },
          {
            path: 'branches',
            loadComponent: () =>
              import('./settings/settings-branches/settings-branches').then(
                (x) => x.SettingsBranches,
              ),
            data: { privileges: ['branch_manage'] },
          },
          {
            path: 'warehouses',
            loadComponent: () =>
              import('./settings/settings-warehouse/settings-warehouse').then(
                (x) => x.SettingsWarehouse,
              ),
          },
          {
            path: 'accounting-policy',
            loadComponent: () =>
              import('./settings/settings-accounting-policy/settings-accounting-policy').then(
                (x) => x.SettingsAccountingPolicy,
              ),
          },

          {
            // balance and payments moved to the subscription page; old links land there
            path: 'balance-payments',
            redirectTo: '/user-main/subscription',
          },
          {
            path: 'resto',
            loadComponent: () =>
              import('./settings/resto-settings/resto-settings').then(
                (x) => x.RestoSettings,
              ),
          },
        ],
      },
      {
        path: 'didox',
        loadComponent: () =>
          import('./warehouse/operations/purchase/didox/didox-main/didox-main').then(
            (x) => x.DidoxMain,
          ),
        // children carry no data of their own: with the default 'emptyOnly'
        // paramsInheritanceStrategy they never inherit it. They are covered because
        // Angular activates this node too, and the shell's canActivateChild runs on
        // every activated node — so entering any /didox/** URL is checked here.
        data: { privileges: ['p15'], moduleName: EDI_INTEGRATION_MODULE },
        children: [
          {
            path: '',
            redirectTo: 'main',
            pathMatch: 'full',
          },
          {
            path: 'auth',
            loadComponent: () =>
              import('./warehouse/operations/purchase/didox/didox-auth/didox-auth').then(
                (x) => x.DidoxAuth,
              ),
          },
          {
            path: 'main',
            canActivate: [didoxGuard],
            loadComponent: () =>
              import('./warehouse/operations/purchase/didox/didox').then(
                (x) => x.Didox,
              ),
          },
          {
            path: 'main/document/:id',
            canActivate: [didoxGuard],
            loadComponent: () =>
              import('./warehouse/operations/purchase/didox/didox-document/didox-document').then(
                (x) => x.DidoxDocument,
              ),
          },
        ],
      },
      {
        path: 'recommended-purchases',
        loadComponent: () =>
          import('./warehouse/recommended-purchases/recommended-purchases').then(
            (x) => x.RecommendedPurchases,
          ),
        data: {
          privileges: ['rec_purchase'],
          moduleName: 'recommended-purchases',
        },
      },
      {
        path: 'summary_report',
        loadComponent: () =>
          import('./reports/summary-reports/summary-reports').then(
            (x) => x.SummaryReports,
          ),
        data: { privileges: ['p28'], moduleName: 'summary_report' },
      },
      {
        path: 'emp-report',
        loadComponent: () =>
          import('./reports/emp-reports/emp-reports').then((x) => x.EmpReports),
        data: { privileges: ['p30'], moduleName: 'emp-report' },
      },
      {
        path: 'sales-report',
        loadComponent: () =>
          import('./reports/sales-reports/sales-reports').then(
            (x) => x.SalesReports,
          ),
        data: { privileges: ['p29'], moduleName: 'sales-report' },
      },
      {
        path: 'abc_reports',
        loadComponent: () =>
          import('./reports/abc-reports/abc-reports').then((x) => x.AbcReports),
        data: { privileges: ['p31'], moduleName: 'abc_reports' },
      },
      {
        path: 'report_generator',
        loadComponent: () =>
          import('./reports/report-generator/report-generator').then(
            (x) => x.ReportGenerator,
          ),
        // module gate only — no `privileges` key, like the multipricing routes
        data: { moduleName: 'report_generator' },
      },
      {
        path: 'analytics',
        loadComponent: () =>
          import('./analytics/analytics').then((x) => x.Analytics),
        data: { privileges: ['p5'], moduleName: 'analytics' },
      },
      {
        path: 'receipts-shifts',
        loadComponent: () =>
          import('./receipts-shifts-main/receipts-shifts-main').then(
            (x) => x.ReceiptsShiftsMain,
          ),
        data: { privileges: ['p42', 'p43'], moduleName: 'receipts-shifts' },
        children: [
          {
            path: '',
            redirectTo: 'receipts',
            pathMatch: 'full',
          },
          {
            path: 'receipts',
            loadComponent: () =>
              import('./receipt-journal/receipt-journal').then(
                (x) => x.ReceiptJournal,
              ),
            data: { privileges: ['p42'] },
          },
          {
            path: 'shifts',
            loadComponent: () =>
              import('./receipts-shifts-main/receipt-shifts/receipt-shifts').then(
                (x) => x.ReceiptShifts,
              ),
            data: { privileges: ['p43'] },
          },
        ],
      },
      ...restaurantRoutes,
      ...deliveryRoutes,
      ...financeRoutes,
      {
        path: 'promo',
        loadComponent: () =>
          import('./marketing/promos/promos').then((x) => x.Promos),
        data: { privileges: ['p27'], moduleName: 'promo' },
      },
      {
        path: 'banner',
        loadComponent: () =>
          import('./marketing/banners/banners').then((x) => x.Banners),
        data: { privileges: ['p27'], moduleName: 'banner' },
      },
      {
        path: 'terminal-operations',
        loadComponent: () =>
          import('./terminal-operations/terminal-operations').then(
            (x) => x.TerminalOperations,
          ),
        data: { privileges: [''], moduleName: 'terminal-operations' },
      },
      {
        // tariff card needs p38, balance and history need p32 (the page gates each);
        // partner-billed orgs never see their billing
        path: 'subscription',
        canActivate: [noPartnerGuard],
        loadComponent: () =>
          import('./subscription/subscription').then((x) => x.Subscription),
        data: { privileges: ['p38', 'p32'], moduleName: 'subscription' },
      },
      {
        path: '**',
        loadComponent: () =>
          import('../../features/unfinished-pages/unfinished-pages').then(
            (m) => m.UnfinishedPages,
          ),
      },
    ],
  },
];
